(function() {
  'use strict';
  
  class ProductCardExpansion {
    constructor() {
      this.activeExpansion = null;
      this.init();
    }
    
    init() {
      document.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('click', this.handleOutsideClick.bind(this));
    }
    
    handleClick(event) {
      // Handle expansion trigger
      const triggerBtn = event.target.closest('[data-card-expansion-trigger]');
      if (triggerBtn) {
        event.preventDefault();
        const uniqueId = triggerBtn.getAttribute('data-card-expansion-trigger');
        this.showExpansion(uniqueId);
        return;
      }
      
      // Handle close button
      const closeBtn = event.target.closest('[data-card-expansion-close]');
      if (closeBtn) {
        event.preventDefault();
        this.hideActiveExpansion();
        return;
      }
    }
    
    handleOutsideClick(event) {
      // Close expansion when clicking outside
      if (this.activeExpansion && !event.target.closest('.product-card-expansion')) {
        if (!event.target.closest('[data-card-expansion-trigger]')) {
          this.hideActiveExpansion();
        }
      }
    }
    
    showExpansion(uniqueId) {
      // Hide any currently active expansion
      this.hideActiveExpansion();
      
      const expansion = document.querySelector(`[data-card-expansion="${uniqueId}"]`);
      if (!expansion) return;
      
      // Show the expansion
      expansion.style.display = 'block';
      // Force reflow before adding class for smooth animation
      expansion.offsetHeight;
      expansion.classList.add('is-visible');
      
      this.activeExpansion = expansion;
      
      // Initialize ProductForm for this expansion
      this.initializeProductForm(expansion, uniqueId);
    }
    
    hideActiveExpansion() {
      if (!this.activeExpansion) return;
      
      const expansion = this.activeExpansion;
      expansion.classList.remove('is-visible');
      
      // Hide after animation completes
      setTimeout(() => {
        expansion.style.display = 'none';
        this.cleanupProductForm(expansion);
      }, 300);
      
      this.activeExpansion = null;
    }
    
    initializeProductForm(expansion, uniqueId) {
      if (!expansion) return;
      
      const productForm = expansion.querySelector('product-form');
      if (!productForm) return;
      
      // Force re-initialization by temporarily removing and re-adding
      const parent = productForm.parentNode;
      if (!parent) return;
      
      const nextSibling = productForm.nextSibling;
      
      // Remove from DOM
      parent.removeChild(productForm);
      
      // Re-add after a brief delay to ensure proper initialization
      setTimeout(() => {
        parent.insertBefore(productForm, nextSibling);
        
        // Set up scoped event listeners for this specific form
        this.setupFormListeners(expansion, uniqueId);
      }, 10);
    }
    
    setupFormListeners(expansion, uniqueId) {
      if (!expansion) return;
      
      const form = expansion.querySelector('form');
      const variantInputs = expansion.querySelectorAll('[data-single-option-selector]');
      const variantIdInput = expansion.querySelector('[data-variant-id-input]');
      const priceElements = expansion.querySelectorAll('[data-expansion-price], [data-expansion-price-btn]');
      const addToCartBtn = expansion.querySelector('[data-add-to-cart]');
      
      if (!form || !variantIdInput) return;
      
      // Get product data
      const productJson = expansion.querySelector('[data-product-json]');
      if (!productJson) return;
      
      let product;
      try {
        product = JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Failed to parse product JSON:', e);
        return;
      }
      
      // Add change listeners to variant option inputs
      variantInputs.forEach(input => {
        if (!input) return; // Guard against null inputs
        
        input.addEventListener('change', () => {
          this.updateVariant(expansion, product, uniqueId);
        });
      });
      
      // Initial update
      this.updateVariant(expansion, product, uniqueId);
    }
    
    updateVariant(expansion, product, uniqueId) {
      const variantInputs = expansion.querySelectorAll('[data-single-option-selector]');
      const variantIdInput = expansion.querySelector('[data-variant-id-input]');
      const priceElements = expansion.querySelectorAll('[data-expansion-price], [data-expansion-price-btn]');
      const addToCartBtn = expansion.querySelector('[data-add-to-cart]');
      
      if (!variantIdInput || !addToCartBtn) {
        console.warn('Required elements not found in product card expansion');
        return;
      }
      
      // Get selected options
      const selectedOptions = {};
      variantInputs.forEach(input => {
        if (!input) return; // Guard against null inputs
        
        if (input.type === 'radio' && input.checked) {
          const optionIndex = input.getAttribute('data-index');
          selectedOptions[optionIndex] = input.value;
        } else if (input.type === 'hidden') {
          const optionIndex = input.getAttribute('data-index');
          selectedOptions[optionIndex] = input.value;
        }
      });
      
      // Find matching variant
      const variant = this.findVariantByOptions(product, selectedOptions);
      
      if (variant) {
        // Update variant ID
        variantIdInput.value = variant.id;
        
        // Check if visual discount is enabled
        const visualDiscountEnabled = window.theme?.settings?.visualDiscountEnable || false;
        const discountPercent = window.theme?.settings?.visualDiscountPercentage || 30;
        
        // Update prices
        priceElements.forEach(priceEl => {
          if (!priceEl) return; // Guard against null price elements
          
          if (visualDiscountEnabled) {
            // Apply visual discount
            const discountedPrice = Math.round(variant.price * (100 - discountPercent) / 100);
            priceEl.innerHTML = `<span class="price--sale">${this.formatMoney(discountedPrice)}</span> <span class="price--compare">${this.formatMoney(variant.price)}</span>`;
          } else if (variant.compare_at_price > variant.price) {
            priceEl.innerHTML = `<span class="price--sale">${this.formatMoney(variant.price)}</span> <span class="price--compare">${this.formatMoney(variant.compare_at_price)}</span>`;
          } else {
            priceEl.textContent = this.formatMoney(variant.price);
          }
        });
        
        // Update option value displays (Select Size: size_name)
        this.updateOptionValueDisplays(expansion, selectedOptions, product);
        
        // Update button state
        if (variant.available) {
          addToCartBtn.disabled = false;
          const addToCartText = addToCartBtn.querySelector('[data-add-to-cart-text]');
          if (addToCartText) {
            if (visualDiscountEnabled) {
              const discountedPrice = Math.round(variant.price * (100 - discountPercent) / 100);
              addToCartText.innerHTML = `Add To Bag - <span class="price--sale">${this.formatMoney(discountedPrice)}</span> <span class="price--compare">${this.formatMoney(variant.price)}</span>`;
            } else {
              addToCartText.innerHTML = `Add To Bag - ${this.formatMoney(variant.price)}`;
            }
          }
        } else {
          addToCartBtn.disabled = true;
          const addToCartText = addToCartBtn.querySelector('[data-add-to-cart-text]');
          if (addToCartText) {
            addToCartText.textContent = 'Sold Out';
          }
        }
      }
    }
    
    updateOptionValueDisplays(expansion, selectedOptions, product) {
      if (!expansion || !selectedOptions) return;
      
      // Update data-option-value elements (for radio button style)
      const optionValueElements = expansion.querySelectorAll('[data-option-value]');
      optionValueElements.forEach((element, index) => {
        if (!element) return; // Guard against null elements
        
        const optionKey = `option${index + 1}`;
        if (selectedOptions[optionKey]) {
          element.textContent = selectedOptions[optionKey];
        }
      });
      
      // Update data-popout-toggle-text elements (for dropdown style)
      const popoutToggleElements = expansion.querySelectorAll('[data-popout-toggle-text]');
      popoutToggleElements.forEach((element, index) => {
        if (!element) return; // Guard against null elements
        
        const optionKey = `option${index + 1}`;
        if (selectedOptions[optionKey]) {
          element.textContent = selectedOptions[optionKey];
        }
      });
      
      // Update popout input values (for dropdown style)
      const popoutInputs = expansion.querySelectorAll('[data-popout-input]');
      popoutInputs.forEach(input => {
        if (!input) return; // Guard against null inputs
        
        const optionIndex = input.getAttribute('data-index');
        if (selectedOptions[optionIndex]) {
          input.value = selectedOptions[optionIndex];
        }
      });
      
      // Update active state in dropdown lists
      const popoutLists = expansion.querySelectorAll('[data-popout-list]');
      popoutLists.forEach((list, optionIndex) => {
        if (!list) return; // Guard against null lists
        
        const items = list.querySelectorAll('[data-popout-option]');
        const optionKey = `option${optionIndex + 1}`;
        const selectedValue = selectedOptions[optionKey];
        
        items.forEach(item => {
          if (!item) return; // Guard against null items
          
          const itemValue = item.getAttribute('data-value');
          const listItem = item.closest('.select-popout__item');
          
          if (!listItem) return; // Guard against null listItem
          
          if (itemValue === selectedValue) {
            listItem.classList.add('is-active');
            item.setAttribute('aria-current', 'true');
          } else {
            listItem.classList.remove('is-active');
            item.removeAttribute('aria-current');
          }
        });
      });
    }
    
    findVariantByOptions(product, selectedOptions) {
      return product.variants.find(variant => {
        return variant.options.every((option, index) => {
          const optionKey = `option${index + 1}`;
          return selectedOptions[optionKey] === option;
        });
      });
    }
    
    formatMoney(cents) {
      // Simple money formatting - you may want to use Shopify's money format
      return '$' + (cents / 100).toFixed(2);
    }
    
    cleanupProductForm(expansion) {
      if (!expansion) return;
      
      const productForm = expansion.querySelector('product-form');
      if (productForm && typeof productForm.destroy === 'function') {
        productForm.destroy();
      }
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductCardExpansion();
    });
  } else {
    new ProductCardExpansion();
  }
})();